/**
 * RLQ — LobbyManagement
 *
 * Supervisor / Manager / Admin view. Same card grid as the queue, but with:
 *   - Filter chips: My team · Branch · All · Unassigned · SLA breach
 *   - Bulk-select via avatar; bulk-action bar appears at the bottom
 *   - Per-card actions row (Claim / Assign / Note / Remove)
 *   - Scope toggle (Branch / All) — Manager+ only
 *
 * Backend status (2026-04-24):
 *   - "Assign…", "Note", and "Remove" are placeholders that emit a
 *     SessionEvents audit row + show a soft toast. The dedicated
 *     `/v3/lobby/{id}/assign|note|remove` routes do NOT exist yet
 *     and are tracked in `docs/wiring/_audit/lobby-management.md` (TBD).
 *   - "Claim" routes the same way the queue claim does.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  AlertTriangle,
  Building2,
  Globe2,
  RefreshCw,
  ShieldAlert,
  Users,
  X,
} from 'lucide-react';

import { Button, FilterChips, Pill } from '@/components/primitives';
import { Toast, type ToastVariant } from '@/components/common/Toast';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type { SessionDto, SessionEventPayload } from '@retail-ops/shared-types';

import {
  buildBranchMap,
  buildServiceMap,
  sessionToVisitor,
  sortLobbyVisitors,
  type LobbyVisitor,
} from '../shared/lobbyData';
import { buildMockLobbySessions, isLobbyMockEnabled } from '../shared/lobbyMock';
import { FALLBACK_SERVICE_TYPES, type ServiceTypeTargetModule } from '../types';
import { LobbyManagementCard } from './LobbyManagementCard';

type MgmtFilter = 'all' | 'unassigned' | 'breach' | 'team';
type MgmtScope = 'branch' | 'all';

interface ToastMessage {
  variant: ToastVariant;
  message: string;
}

export default function LobbyManagement(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<ReadonlyArray<SessionDto>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);
  const [filter, setFilter] = useState<MgmtFilter>('all');
  const [scope, setScope] = useState<MgmtScope>('branch');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toastMsg, setToastMsg] = useState<ToastMessage | null>(null);

  const isManagerPlus = ['Manager', 'Executive', 'Admin', 'SuperAdmin'].includes(
    user?.primaryRole ?? '',
  );

  const serviceMap = useMemo(() => buildServiceMap(FALLBACK_SERVICE_TYPES), []);
  const branchMap = useMemo(() => buildBranchMap(config.branches), [config.branches]);

  const activeBranch = user?.activeBranchCode ?? user?.branchCode ?? '';

  // Fetch -------------------------------------------------------------------
  useEffect(() => {
    if (isLobbyMockEnabled()) {
      setSessions(buildMockLobbySessions());
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    client.retail.sessions
      .list({ status: 'active', top: 200 }, { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        const lobby = (res.sessions ?? []).filter(
          (s) => s.sessionType === 'lobby' || s.origin === 'kiosk',
        );
        setSessions(lobby);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setSessions([]);
        setError(err instanceof Error ? err.message : 'Unable to load lobby management.');
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [client, tick]);

  // Adapt + scope + filter --------------------------------------------------
  const visitors = useMemo<ReadonlyArray<LobbyVisitor>>(() => {
    let walkInIndex = 0;
    let scoped = sessions.slice();
    if (scope === 'branch' && activeBranch) {
      scoped = scoped.filter((s) => s.branch === activeBranch);
    }
    const adapted = scoped.map((s) => {
      walkInIndex += 1;
      return sessionToVisitor(s, { serviceMap, branchMap, walkInIndex });
    });
    const filtered = adapted.filter((v) => {
      if (filter === 'unassigned') return !v.assignedOfficerName;
      if (filter === 'breach') return v.waitTier === 'warning' || v.waitTier === 'breached';
      if (filter === 'team') {
        const team = (user?.subordinatesEmails ?? []).map((e) => e.toLowerCase());
        if (team.length === 0) return false;
        if (!v.assignedOfficerName) return false;
        const officer = v.assignedOfficerName.toLowerCase();
        return team.some((email) => officer.includes(email.split('@')[0] ?? ''));
      }
      return true;
    });
    return sortLobbyVisitors(filtered);
  }, [sessions, scope, activeBranch, serviceMap, branchMap, filter, user?.subordinatesEmails]);

  // Counts ------------------------------------------------------------------
  const counts = useMemo(() => {
    let unassigned = 0;
    let breach = 0;
    for (const v of visitors) {
      if (!v.assignedOfficerName) unassigned += 1;
      if (v.waitTier === 'warning' || v.waitTier === 'breached') breach += 1;
    }
    return { total: visitors.length, unassigned, breach };
  }, [visitors]);

  // Selection ---------------------------------------------------------------
  const toggleSelect = (v: LobbyVisitor): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(v._sessionId)) next.delete(v._sessionId);
      else next.add(v._sessionId);
      return next;
    });
  };
  const clearSelection = (): void => setSelectedIds(new Set());
  const selectionCount = selectedIds.size;

  // Action handlers ---------------------------------------------------------
  const auditAction = (sessionId: string, action: string, detail?: unknown): void => {
    const payload: SessionEventPayload = {
      sessionId,
      eventType: `lobby-mgmt-${action}`,
      eventCategory: 'session',
      severity: 'info',
      actor: user?.upn ?? 'supervisor',
      timestamp: asIso8601(new Date().toISOString()),
      payloadJson: JSON.stringify({ action, detail: detail ?? null }),
    };
    client.retail.events.write(payload).catch(() => {
      // non-blocking
    });
  };

  const handleClaim = (v: LobbyVisitor): void => {
    const target: ServiceTypeTargetModule = v.targetModule ?? 'RCC';
    const qp: string[] = [`target=${target}`];
    if (v._cifId) qp.push(`cifId=${encodeURIComponent(v._cifId)}`);
    if (v._cartId) qp.push(`cartId=${encodeURIComponent(v._cartId)}`);
    navigate(`/rlq/claim/${encodeURIComponent(v._sessionId)}?${qp.join('&')}`);
  };

  const handleAssign = (v: LobbyVisitor): void => {
    auditAction(v._sessionId, 'assign-requested');
    setToastMsg({
      variant: 'info',
      message: 'Assignment workflow is queued — needs the /v3/lobby/{id}/assign route.',
    });
  };

  const handleNote = (v: LobbyVisitor): void => {
    auditAction(v._sessionId, 'note-requested');
    setToastMsg({
      variant: 'info',
      message: 'Note saved to the session audit log.',
    });
  };

  const handleRemove = (v: LobbyVisitor): void => {
    auditAction(v._sessionId, 'remove-requested', { reason: 'supervisor-remove' });
    setToastMsg({
      variant: 'warn',
      message:
        'Remove queued — visitor will drop off the queue once the dedicated lobby route lands.',
    });
  };

  const handleBulkAssign = (): void => {
    for (const id of selectedIds) auditAction(id, 'assign-requested', { bulk: true });
    setToastMsg({
      variant: 'info',
      message: `Queued ${selectionCount} assignments (pending backend route).`,
    });
    clearSelection();
  };

  const handleBulkRemove = (): void => {
    for (const id of selectedIds) auditAction(id, 'remove-requested', { bulk: true });
    setToastMsg({
      variant: 'warn',
      message: `Queued removal for ${selectionCount} visitors.`,
    });
    clearSelection();
  };

  // -------------------------------------------------------------------------
  return (
    <div className="lobby-page">
      <header className="lobby-page__header">
        <div className="lobby-page__title-row">
          <h1 className="lobby-page__title">Lobby management</h1>
          <Pill variant="muted">Supervisor view</Pill>
          {counts.breach > 0 && (
            <Pill variant="err" icon={<ShieldAlert size={11} />}>
              {counts.breach} need attention
            </Pill>
          )}
          {error && <Pill variant="err">Load error</Pill>}
        </div>
        <p className="lobby-page__subtitle">
          Bird's-eye of every lobby visitor. Reassign, escalate, or clear in
          bulk; jump into a customer with one click.
        </p>
      </header>

      <div className="lobby-toolbar">
        <FilterChips
          label="Filter"
          options={[
            { value: 'all', label: `All (${visitors.length})` },
            { value: 'unassigned', label: `Unassigned (${counts.unassigned})` },
            { value: 'breach', label: `Watching SLA (${counts.breach})` },
            ...(user?.subordinatesEmails && user.subordinatesEmails.length > 0
              ? [{ value: 'team' as const, label: 'My team' }]
              : []),
          ]}
          selected={filter}
          onChange={(v) => setFilter(v as MgmtFilter)}
        />

        {isManagerPlus && (
          <FilterChips
            label="Scope"
            options={[
              { value: 'branch', label: 'Branch' },
              { value: 'all', label: 'All branches' },
            ]}
            selected={scope}
            onChange={(v) => setScope(v as MgmtScope)}
          />
        )}

        <span className="lobby-toolbar__scope-hint">
          {scope === 'all' ? (
            <>
              <Globe2 size={11} aria-hidden /> All branches
            </>
          ) : (
            <>
              <Building2 size={11} aria-hidden />{' '}
              {branchMap.get(activeBranch)?.branchName ?? `Branch ${activeBranch || '—'}`}
            </>
          )}
        </span>

        <Button
          variant="ghost"
          size="sm"
          startIcon={<RefreshCw size={12} />}
          onClick={() => setTick((n) => n + 1)}
        >
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="lobby-empty">Loading lobby management…</div>
      ) : visitors.length === 0 ? (
        <div className="lobby-empty lobby-empty--art">
          <div className="lobby-empty__art" aria-hidden>
            <Users size={28} />
          </div>
          <div className="lobby-empty__title">Nothing to manage right now</div>
          <div className="lobby-empty__hint">
            New arrivals will land here as they check in.
          </div>
        </div>
      ) : (
        <div className="rop-card-grid rop-card-grid--lg">
          {visitors.map((v) => (
            <LobbyManagementCard
              key={v._sessionId}
              visitor={v}
              selected={selectedIds.has(v._sessionId)}
              onToggleSelect={toggleSelect}
              onClaim={handleClaim}
              onAssign={handleAssign}
              onNote={handleNote}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}

      {selectionCount > 0 && (
        <div className="lobby-bulk-bar" role="region" aria-label="Bulk actions">
          <span className="lobby-bulk-bar__label">
            <AlertTriangle size={12} aria-hidden /> {selectionCount} selected
          </span>
          <Button size="sm" variant="primary" onClick={handleBulkAssign}>
            Assign…
          </Button>
          <Button size="sm" variant="ghost" onClick={handleBulkRemove}>
            Remove from lobby
          </Button>
          <Button
            size="sm"
            variant="ghost"
            startIcon={<X size={12} />}
            onClick={clearSelection}
          >
            Clear
          </Button>
        </div>
      )}

      {toastMsg && (
        <div className="lobby-toast-anchor">
          <Toast
            variant={toastMsg.variant}
            message={toastMsg.message}
            autoHideMs={3500}
            onClose={() => setToastMsg(null)}
          />
        </div>
      )}
    </div>
  );
}
